import { ChartAPIReturn, EventsConfig } from '@toggle/chart';
import {
  ChartInsight,
  isCatalystInsight,
  isEarningsInsight,
} from '@toggle/toggle';
import React, { FC, RefObject, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useEventFeed } from '~/react-query-hooks/events/use-event-feed/useEventFeed';

import { useInsightsInDomain } from '../../hooks/use-insights-in-domain/useInsightsInDomain';
import { ChartInsightIcon } from './chart-insight-icon/ChartInsightIcon';
import * as S from './Insights.styles';

export interface InsightsProps {
  chartApiRef: RefObject<ChartAPIReturn | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  insights: ChartInsight[];
  eventsConfig: EventsConfig;
  isSimpleChart?: boolean;
  chartWidth?: number;
  chartHeight?: number;
}

export const Insights: FC<InsightsProps> = ({
  chartApiRef,
  canvasRef,
  insights,
  eventsConfig,
  isSimpleChart,
  chartWidth,
  chartHeight,
}) => {
  const { t } = useTranslation(['chart']);

  const { insightsInDomain, zoomIntoViewText } = useInsightsInDomain({
    canvasRef,
    eventsConfig,
    chartApiRef,
    insights,
    chartWidth,
    chartHeight,
  });

  const earningsEntityTags = useMemo(
    () => [
      ...new Set(
        insights.filter(isEarningsInsight).map(i => i.entity_tag)
      ),
    ],
    [insights]
  );
  const { data: earningsEventFeedData } = useEventFeed(
    {
      entities: earningsEntityTags,
      type: 'earnings-insight',
    },
    { enabled: earningsEntityTags.length > 0 }
  );
  const earningsEvents = useMemo(
    () => earningsEventFeedData?.pages?.flatMap(page => page.events) ?? [],
    [earningsEventFeedData?.pages]
  );

  const catalystEntityTags = useMemo(
    () => [
      ...new Set(
        insights
          .filter(isCatalystInsight)
          .map(i => i.identifier)
          .filter(Boolean)
      ),
    ],
    [insights]
  );
  const { data: catalystEventFeedData } = useEventFeed(
    {
      entities: catalystEntityTags,
      type: 'attribution-insight',
      filters: {
        company: {
          values: [
            { category: 'corporate_actions_strategic_announcements' },
            { category: 'management_leadership_changes' },
            { category: 'financial_operational_updates' },
            { category: 'legal_regulatory_events' },
          ],
        },
      },
    },
    { enabled: catalystEntityTags.length > 0 }
  );
  const catalystEvents = useMemo(
    () => catalystEventFeedData?.pages?.flatMap(page => page.events) ?? [],
    [catalystEventFeedData?.pages]
  );

  return (
    <>
      {insightsInDomain.map((domainEntry, insightsInDomainIndex) => (
        <ChartInsightIcon
          key={insightsInDomainIndex}
          insightIconRight={domainEntry.right}
          insightIconTop={domainEntry.top}
          yOffsetCount={domainEntry.yOffsetCount}
          groupedInsights={domainEntry.groupedInsights}
          isSimpleChart={isSimpleChart}
          catalystEvents={catalystEvents}
          earningsEvents={earningsEvents}
        />
      ))}

      {zoomIntoViewText && (
        <S.ZoomIntoViewText
          data-testid="zoom-in-required"
          style={{
            top: zoomIntoViewText.y,
            right: zoomIntoViewText.right,
          }}
        >
          {t('chart:zoomInToView')}
        </S.ZoomIntoViewText>
      )}
    </>
  );
};
